/**
 * SaaS Admin — gobernanza de métricas Analytics (capabilities) por tenant.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import type { AnalyticsMetricDef, AnalyticsMetricAccess } from "@/lib/analytics-metric-catalog";
import { AdminNicheAnalyticsDashboard } from "./AdminNicheAnalyticsDashboard";

type TenantOption = { id: string; name: string; slug: string };

type Payload = {
  catalog: AnalyticsMetricDef[];
  overrides: Record<string, boolean>;
  access: Record<string, AnalyticsMetricAccess>;
  planId: string;
};

export function AdminAnalyticsMetricsPanel() {
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [tenantId, setTenantId] = useState<string>("");
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    apiGetJson<TenantOption[] | { tenants: TenantOption[] }>("/api/admin/tenants")
      .then((r) =>
        setTenants((Array.isArray(r) ? r : (r.tenants ?? [])).map((t) => ({ id: t.id, name: t.name, slug: t.slug }))),
      )
      .catch((e) => toast.error(e instanceof Error ? e.message : "Error tenants"));
  }, []);

  const load = useCallback(async (id: string) => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await apiGetJson<Payload>(
        `/api/admin/tenant-analytics-metrics?tenantId=${encodeURIComponent(id)}`,
      );
      setData(r);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar métricas");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tenantId) void load(tenantId);
  }, [tenantId, load]);

  const groupedByModule = useMemo(() => {
    if (!data) return [];
    const groups = new Map<string, AnalyticsMetricDef[]>();
    for (const m of data.catalog) {
      const key = m.module || "General";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(m);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  const patch = async (key: string, value: boolean | null) => {
    if (!tenantId) return;
    setSaving(key);
    try {
      await apiSendJson<{ overrides: Record<string, boolean> }>(
        "/api/admin/tenant-analytics-metrics",
        "PATCH",
        { tenantId, key, value },
      );
      await load(tenantId);
      toast.success("Override actualizado");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Analytics · métricas (capabilities)</h2>
        <p className="text-sm text-muted-foreground">
          Goberná módulos/KPIs por plan, perfil, flag <code>analytics_advanced</code> y override por
          tenant (mismo modelo mental que Feature Flags).
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={tenantId || undefined} onValueChange={setTenantId}>
          <SelectTrigger className="w-[280px]">
            <SelectValue placeholder="Elegir tenant" />
          </SelectTrigger>
          <SelectContent>
            {tenants.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data ? (
          <span className="text-xs text-muted-foreground">Plan efectivo: {data.planId}</span>
        ) : null}
      </div>
      {tenantId ? <AdminNicheAnalyticsDashboard tenants={tenants} tenantId={tenantId} /> : null}

      <p className="text-xs text-muted-foreground">
        Gobernanza de capabilities abajo. KPIs de negocio por nicho arriba (OTIF contractual, stockouts, QR mesa, etc.).
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
        </div>
      ) : null}

      {data && !loading ? (
        <>
          {/* Cards en mobile — agrupadas por módulo */}
          <div className="flex flex-col gap-4 md:hidden">
            {groupedByModule.map(([module, items]) => (
              <div key={module} className="flex flex-col gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {module} <span className="font-normal normal-case">({items.length})</span>
                </h3>
                {items.map((m) => {
                  const acc = data.access[m.id];
                  const ov = data.overrides[m.id];
                  return (
                    <div key={m.id} className="rounded-xl border border-border p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-sm">{m.label}</p>
                          <p className="text-[11px] text-muted-foreground break-all">{m.id}</p>
                        </div>
                        {acc?.enabled ? (
                          <span className="shrink-0 text-xs text-emerald-600">ON</span>
                        ) : (
                          <span className="shrink-0 text-xs text-muted-foreground">OFF</span>
                        )}
                      </div>
                      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        <dt className="text-muted-foreground">Min plan</dt>
                        <dd>{m.minPlan}</dd>
                        <dt className="text-muted-foreground">Override</dt>
                        <dd>{ov === true ? "force ON" : ov === false ? "force OFF" : "—"}</dd>
                      </dl>
                      {!acc?.enabled && acc?.reason ? (
                        <p className="mt-1 text-[11px] text-muted-foreground">Motivo: {acc.reason}</p>
                      ) : null}
                      <div className="mt-2 flex flex-wrap gap-1">
                        <Button size="sm" variant="outline" disabled={saving === m.id} onClick={() => void patch(m.id, true)}>
                          ON
                        </Button>
                        <Button size="sm" variant="outline" disabled={saving === m.id} onClick={() => void patch(m.id, false)}>
                          OFF
                        </Button>
                        <Button size="sm" variant="ghost" disabled={saving === m.id} onClick={() => void patch(m.id, null)}>
                          Reset
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Tabla en desktop */}
          <div className="hidden overflow-x-auto rounded-md border border-border md:block">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="p-2">Métrica</th>
                <th className="p-2">Min plan</th>
                <th className="p-2">Default</th>
                <th className="p-2">Override</th>
                <th className="p-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {groupedByModule.map(([module, items]) => (
                <Fragment key={module}>
                  <tr className="border-t border-border/60 bg-muted/20">
                    <td colSpan={5} className="p-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {module} <span className="font-normal normal-case">({items.length})</span>
                    </td>
                  </tr>
                  {items.map((m) => {
                const acc = data.access[m.id];
                const ov = data.overrides[m.id];
                return (
                  <tr key={m.id} className="border-t border-border/60">
                    <td className="p-2">
                      <div className="font-medium">{m.label}</div>
                      <div className="text-[11px] text-muted-foreground">{m.id}</div>
                    </td>
                    <td className="p-2">{m.minPlan}</td>
                    <td className="p-2">
                      {acc?.enabled ? (
                        <span className="text-emerald-600">ON</span>
                      ) : (
                        <span className="text-muted-foreground">OFF ({acc?.reason})</span>
                      )}
                    </td>
                    <td className="p-2">
                      {ov === true ? "force ON" : ov === false ? "force OFF" : "—"}
                    </td>
                    <td className="p-2">
                      <div className="flex flex-wrap gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={saving === m.id}
                          onClick={() => void patch(m.id, true)}
                        >
                          ON
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={saving === m.id}
                          onClick={() => void patch(m.id, false)}
                        >
                          OFF
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={saving === m.id}
                          onClick={() => void patch(m.id, null)}
                        >
                          Reset
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
